/**
 * Mobile/tablet inventory card list. Same delegation pattern as ProductTable.
 */
import { Package, Image as ImageIcon, CurrencyDollar, MegaphoneSimple, Eye, ClockClockwise, Pencil, Trash, Sparkle } from "@phosphor-icons/react";
import { fmt } from "@/lib/api";
import { RowCheckbox } from "@/components/BulkActionBar";

export function ProductCardList({
  filteredItems,
  query,
  selection,
  postCounts,
  isAdmin,
  setGallery,
  setAudienceProduct,
  goToPublic,
  goToCreatePost,
  viewProductPosts,
  sellNow,
  viewHistory,
  onEdit,
  onDelete,
}) {
  return (
    <section className="lg:hidden space-y-3" data-testid="inventory-cards">
        {filteredItems.length === 0 && (
          <div className="border border-zinc-200 px-4 py-12 text-center text-sm text-muted2">
            <Package size={28} className="mx-auto mb-3 text-zinc-400" />
            {query ? `No hay productos que coincidan con "${query}".` : "Aún no tienes productos. Agrega uno o escanea un objeto."}
          </div>
        )}
        {filteredItems.map((p) => {
          const utilidad = (p.precio_venta - p.costo) * p.cantidad;
          const imgs = (p.imagenes_base64 && p.imagenes_base64.length
            ? p.imagenes_base64
            : p.imagen_base64
            ? [p.imagen_base64]
            : []);
          const thumb = imgs[0] ? `data:image/jpeg;base64,${imgs[0]}` : null;
          return (
            <div
              key={p.id}
              className={`border border-zinc-200 p-3 flex gap-3 ${selection.isSelected(p.id) ? "bg-[#EEF2FF]" : ""}`}
              data-testid={`product-card-${p.id}`}
            >
              <div className="shrink-0 pt-1">
                <RowCheckbox
                  checked={selection.isSelected(p.id)}
                  onChange={() => selection.toggle(p.id)}
                  testid={`m-select-${p.id}`}
                />
              </div>
              <button
                onClick={() =>
                  imgs.length &&
                  setGallery({
                    product_id: p.id,
                    images: imgs,
                    originals: p.imagenes_originales_base64 || [],
                    idx: 0,
                    nombre: p.nombre,
                  })
                }
                className={`relative shrink-0 w-16 h-16 border border-zinc-200 overflow-hidden bg-zinc-50 flex items-center justify-center ${
                  imgs.length ? "cursor-zoom-in hover:border-klein" : ""
                }`}
                disabled={!imgs.length}
              >
                {thumb ? (
                  <>
                    <img src={thumb} alt={p.nombre} className="w-full h-full object-cover" />
                    {imgs.length > 1 && (
                      <span className="absolute bottom-0 right-0 text-[9px] bg-klein text-white px-1 leading-tight font-mono">
                        {imgs.length}
                      </span>
                    )}
                  </>
                ) : (
                  <ImageIcon size={18} className="text-zinc-400" />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => goToPublic(p)}
                      className="font-medium text-sm truncate text-left hover:text-klein hover:underline"
                      data-testid={`m-name-${p.id}`}
                    >
                      {p.nombre}
                    </button>
                    <div className="text-[11px] text-muted2 truncate">{p.descripcion || p.para_que_sirve}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => sellNow(p)}
                      className="p-1.5 hover:bg-zinc-100 text-[#00C853]"
                      data-testid={`m-sell-now-${p.id}`}
                      title="Vender ahora"
                    >
                      <CurrencyDollar size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => goToCreatePost(p)}
                      className="p-1.5 hover:bg-zinc-100 text-klein"
                      data-testid={`m-create-post-${p.id}`}
                      title="Crear publicación"
                    >
                      <MegaphoneSimple size={14} />
                    </button>
                    <button
                      onClick={() => setAudienceProduct(p)}
                      className="p-1.5 hover:bg-zinc-100 text-[#7C3AED]"
                      data-testid={`m-audience-${p.id}`}
                      title="Investigar audiencia con IA · dónde publicar este producto"
                    >
                      <Sparkle size={14} />
                    </button>
                    {postCounts[p.id] > 0 && (
                      <button
                        onClick={() => viewProductPosts(p)}
                        className="px-1.5 hover:bg-klein hover:text-white text-klein border border-klein/40 text-[10px] font-mono font-bold tabular-nums leading-none flex items-center"
                        data-testid={`m-view-posts-${p.id}`}
                        title={`${postCounts[p.id]} publicación(es)`}
                      >
                        {postCounts[p.id]}
                      </button>
                    )}
                    <button
                      onClick={() => goToPublic(p)}
                      className="p-1.5 hover:bg-zinc-100 text-klein"
                      data-testid={`m-view-${p.id}`}
                      title="Ver página pública"
                    >
                      <Eye size={14} />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => viewHistory(p)}
                        className="p-1.5 hover:bg-zinc-100 text-zinc-600"
                        data-testid={`m-history-${p.id}`}
                        title="Ver historial"
                      >
                        <ClockClockwise size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(p)}
                      className="p-1.5 hover:bg-zinc-100"
                      data-testid={`m-edit-${p.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
                      data-testid={`m-delete-${p.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5 text-[11px]">
                  <span className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200">{p.categoria}</span>
                  <span className="px-1.5 py-0.5 text-muted2">{p.condicion}</span>
                  <span className="px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 tabular-nums">×{p.cantidad}</span>
                </div>
                <div className="grid grid-cols-3 gap-1 mt-2 text-[11px] tabular-nums">
                  <div>
                    <div className="overline">Costo</div>
                    <div>{fmt(p.costo)}</div>
                  </div>
                  <div>
                    <div className="overline">Venta</div>
                    <div>{fmt(p.precio_venta)}</div>
                  </div>
                  <div>
                    <div className="overline">Utilidad</div>
                    <div className={`font-medium ${utilidad >= 0 ? "text-[#00C853]" : "text-[#FF2A00]"}`}>
                      {fmt(utilidad)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>
  );
}
